import { FC, useMemo } from 'react'
import styled from 'styled-components'

import { TypeStyle } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { EmbroideryService } from '../../../../../containers/EmbroideryService'
import { LogoFile } from '../../../../../containers/EmbroideryService/types'
import { ErrorMessage, Label } from '../../shared-form-styles'
import { LogoFormContainer } from '../LogoFormContainer'

import { AddNewLogoButton } from './AddNewLogoButton'
import * as LogoPreviewItem from './LogoPreviewItem'

export const LogoSelector: FC = () => {
  const {
    embroidery: {
      modal: {
        forms: { dtcLogoEmbroideryForm: fixtures, logoOption },
      },
    },
  } = useTranslation()

  const { selectedEmbroideryOptions } = EmbroideryService.useContainer()
  const { addLogoForm, logoFiles, hasActiveLogoSetup, transientLogo, setToggleDisplay } =
    LogoFormContainer.useContainer()

  // TODO: need to confirm but the API may only return 'PENDING' state only even though there are multiple states
  const logoStates = {
    pending: [
      'PENDING',
      'SENT_TO_DIGITIZER',
      'DIGITIZATION_UNDER_REVIEW',
      'SENT_FOR_CUSTOMER_APPROVAL',
      'CUSTOMER_FEEDBACK',
    ],
    approved: ['APPROVED'],
    canceled: ['CANCELED'],
  }

  const handleLogoSelection = (selectedLogo: LogoFile) => {
    addLogoForm.setFieldValue('logoFile', selectedLogo)
    addLogoForm.setFieldValue('logoFileId', selectedLogo.id)
  }

  const logoColorOptionTitle = useMemo(() => {
    const digitizationSetting = addLogoForm?.values?.logoFile?.digitizationSetting
    if (!digitizationSetting) return undefined
    return digitizationSetting === 'BLACK_WHITE'
      ? ` (${fixtures.logoStrings.LOGO_BLACK_WHITE?.title})`
      : digitizationSetting === 'COLOR'
      ? ` (${fixtures.logoStrings.LOGO_COLOR?.title})`
      : ''
  }, [fixtures.logoStrings, addLogoForm?.values?.logoFile?.digitizationSetting])

  const canAddNewLogo = useMemo(() => {
    // If a transient logo exists in the cart, don't allow new logo requests
    if (transientLogo) return false
    // If there is an active logo setup, don't allow new logo requests
    if (hasActiveLogoSetup) return false
    return true
  }, [transientLogo, hasActiveLogoSetup])

  return (
    <>
      <StyledLabel id='logo-placement-group'>
        {logoOption.logoLabel}
        <SelectedLogoLabel>
          {addLogoForm.values.logoFile?.name}
          {logoColorOptionTitle}
        </SelectedLogoLabel>
      </StyledLabel>
      <LogoPreviewItem.GridContainer>
        {logoFiles.length > 0 &&
          logoFiles.map(logo => {
            const isEditable = selectedEmbroideryOptions?.LOGO_SETUP?.logoFile?.id === logo.id
            const disabled = logoStates.pending.includes(logo.status) && !isEditable
            const isApprovedLogo = logo.status === 'APPROVED'
            return (
              <div role='group' aria-labelledby='logo-placement-group' key={logo.id}>
                <LogoPreviewItem.Container>
                  <LogoPreviewItem.Label>
                    <LogoPreviewItem.Input
                      type='radio'
                      name='logoFileId'
                      disabled={disabled}
                      checked={addLogoForm.values.logoFileId === logo.id}
                      value={logo.id}
                      onChange={event => {
                        if (event.target.checked) {
                          handleLogoSelection(logo)
                        }
                      }}
                    />

                    <LogoPreviewItem.Image
                      src={logo.preview}
                      aspectRatios={{
                        sm: null,
                        md: null,
                      }}
                      loading='lazy'
                      alt={logo.name}
                      widths={{ unit: 'vw', sm: 100, md: 50 }}
                      transparentBackground={true}
                    />
                  </LogoPreviewItem.Label>
                  {!isApprovedLogo && isEditable ? (
                    <LogoPreviewItem.Status>{fixtures.digitizationRequest}</LogoPreviewItem.Status>
                  ) : logoStates.pending.includes(logo.status) ? (
                    <LogoPreviewItem.Status>{fixtures.digitizationPending}</LogoPreviewItem.Status>
                  ) : null}
                </LogoPreviewItem.Container>
                {!isApprovedLogo && hasActiveLogoSetup && (
                  <LogoPreviewItem.Footer>
                    <LogoPreviewItem.FooterDescription>
                      {logo.notes && <>{fixtures.noteIncluded}</>}
                    </LogoPreviewItem.FooterDescription>
                    {isEditable && (
                      <LogoPreviewItem.FooterLink
                        type='button'
                        onClick={() => setToggleDisplay(true)}
                      >
                        {fixtures.edit}
                      </LogoPreviewItem.FooterLink>
                    )}
                  </LogoPreviewItem.Footer>
                )}
              </div>
            )
          })}
        {canAddNewLogo ? <AddNewLogoButton /> : null}
      </LogoPreviewItem.GridContainer>
      {addLogoForm.errors['logoFileId'] && (
        <ErrorMessage inset role='alert'>
          {addLogoForm.errors['logoFileId']}
        </ErrorMessage>
      )}
    </>
  )
}

const StyledLabel = styled(Label)`
  margin-top: ${o => o.theme.spacing(6)};
`

const SelectedLogoLabel = styled.span`
  ${TypeStyle.css.headlineMicro}
  padding-left: ${({ theme }) => theme.spacing(1)};
  text-transform: none;
  color: ${o => o.theme.color.deprecated.obsidianLight};
  font-weight: ${o => o.theme.typography.font.weight[400]};
`
